import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EditorContent, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Globe,
  Newspaper,
  PaperclipIcon,
  Send,
  Square,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import tippy, {
  GetReferenceClientRect,
  Instance as TippyInstance,
} from 'tippy.js';

import { useEditorContext } from '@/components/demands-v2/editor/context/editor-provider';
import { AgentChatPlugin } from '@/components/demands-v2/editor/plugins/agent-chat-plugin/agent-chat-plugin';

import { useGetDemandExampleQuery } from '../../../services/api/demandExampleService';
import { AgentChatAttachment } from '../../../services/types/agent-chat-types';
import { DemandExampleSelector } from '../../demand-example/DemandExampleSelector';
import { Button } from '../../ui/catalyst/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { Z_INDEX_FINCH_ASSISTANT } from '../../ui/zIndex';
import { SectionAnalysis } from '../demand-example/SectionAnalysis';
import {
  PendingMessage,
  StreamOptions,
} from '../hooks/useAgentChatStream';
import { useOneShotDemand } from '../hooks/useOneShotDemand';
import {
  SlashCommandExtension,
  SlashCommandImportDialog,
  SlashCommandManageDialog,
  SlashCommandSuggestionList,
  useSlashCommandImport,
  useSlashCommands,
} from '../slash-commands';
import { SlashCommandSuggestionItem } from '../slash-commands/types';
import { AttachmentBadge } from './AttachmentBadge';

interface MessageInputProps {
  matterId: string;
  threadId: string;
  onUploadAttachment: (file: File) => Promise<AgentChatAttachment>;
  onDeleteAttachment: (attachmentId: string) => Promise<void>;
  isDisabled?: boolean;
  isSending: boolean;
  isStreaming: boolean;
  sendMessage: (
    messageHtml: string,
    attachments: AgentChatAttachment[],
    opts?: StreamOptions,
  ) => Promise<void>;
  stopRun: () => Promise<void>;
  restoredMessage: PendingMessage | null;
  demandGenStatus?: 'idle' | 'running' | 'completed';
  onFirstMessage?: () => void;
}

interface PendingAttachment {
  id: string;
  name: string;
  size: number;
  file: File;
  isUploading: boolean;
}

export const MessageInput = ({
  matterId,
  threadId,
  onUploadAttachment,
  onDeleteAttachment,
  isDisabled,
  isSending,
  isStreaming,
  sendMessage,
  stopRun,
  restoredMessage,
  demandGenStatus,
  onFirstMessage,
}: MessageInputProps) => {
  const isAgentResponding = isStreaming || demandGenStatus === 'running';

  // Web search toggle (local state per MessageInput instance)
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);

  const [exampleDemandLetterId, setExampleDemandLetterId] = useState<
    string | null
  >(null);
  const [oneShotDemandLetterId, setOneShotDemandLetterId] = useState<
    string | null
  >(null);
  const [attachments, setAttachments] = useState<AgentChatAttachment[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [isDemandSelectorOpen, setIsDemandSelectorOpen] = useState(false);
  const [isOneShotSelectorOpen, setIsOneShotSelectorOpen] = useState(false);
  const [isSubmittingAnalysis, setIsSubmittingAnalysis] = useState(false);

  // Slash commands
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const {
    commands: slashCommands,
    addCommand,
    updateCommand,
    deleteCommand,
    importCommands,
  } = useSlashCommands();
  const slashCommandsRef = useRef(slashCommands);
  useEffect(() => {
    slashCommandsRef.current = slashCommands;
  }, [slashCommands]);
  const isSuggestionActiveRef = useRef(false);
  const {
    importPayload,
    isImportDialogOpen,
    setIsImportDialogOpen,
    dismissImport,
  } = useSlashCommandImport();

  const {
    isAnalyzingSections,
    isSubmittingSections,
    analyzedSections,
    showSectionsDialog,
    handleOneShotDemandSelected,
    handleSubmitSections,
    setShowSectionsDialog,
  } = useOneShotDemand({
    matterId,
    threadId,
    onUploadAttachment,
    sendMessage,
    isStreaming,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      StarterKit.configure({
        document: false,
        paragraph: false,
        text: false,
        hardBreak: {},
      }),
      SlashCommandExtension.configure({
        suggestion: {
          char: '/',
          allowedPrefixes: null,
          items: ({ query }: { query: string }) => {
            const lowerQuery = query.toLowerCase();
            return slashCommandsRef.current
              .filter(
                (cmd) =>
                  cmd.name.toLowerCase().includes(lowerQuery) ||
                  cmd.description.toLowerCase().includes(lowerQuery),
              )
              .slice(0, 10)
              .map(
                (cmd): SlashCommandSuggestionItem => ({
                  id: cmd.id,
                  name: cmd.name,
                  description: cmd.description,
                  promptText: cmd.promptText,
                }),
              );
          },
          render: () => {
            let component: ReactRenderer<any>;
            let popup: TippyInstance[];

            return {
              onStart: (props: any) => {
                isSuggestionActiveRef.current = true;
                component = new ReactRenderer(SlashCommandSuggestionList, {
                  props: {
                    ...props,
                    onManageCommands: () => {
                      popup[0].hide();
                      setIsManageDialogOpen(true);
                    },
                  },
                  editor: props.editor,
                });

                if (!props.clientRect) return;

                popup = tippy('body', {
                  getReferenceClientRect:
                    props.clientRect as GetReferenceClientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'top-start',
                  offset: [0, 8],
                  zIndex: Z_INDEX_FINCH_ASSISTANT + 10,
                });
              },

              onUpdate: (props: any) => {
                component.updateProps({
                  ...props,
                  onManageCommands: () => {
                    popup[0].hide();
                    setIsManageDialogOpen(true);
                  },
                });

                if (!props.clientRect) return;
                popup[0].setProps({
                  getReferenceClientRect:
                    props.clientRect as GetReferenceClientRect,
                });
              },

              onKeyDown: (props: any) => {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }
                return component.ref?.onKeyDown(props) ?? false;
              },

              onExit: () => {
                isSuggestionActiveRef.current = false;
                popup[0].destroy();
                component.destroy();
              },
            };
          },
          command: ({
            editor: cmdEditor,
            range,
            props,
          }: {
            editor: any;
            range: any;
            props: SlashCommandSuggestionItem;
          }) => {
            cmdEditor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent(props.promptText)
              .run();
          },
        },
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class:
          'min-h-[60px] p-3 text-sm focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
      },
    },
    editable: !isAgentResponding,
  });

  // Add the RTK Query hook at the top level
  const { data: selectedDemandExample } = useGetDemandExampleQuery(
    oneShotDemandLetterId!,
    {
      skip: !oneShotDemandLetterId,
    },
  );

  // Update editor editable state when isChatInProgress changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!isAgentResponding);
    }
  }, [isAgentResponding, editor]);

  // Focus editor when agent response completes
  useEffect(() => {
    if (!isAgentResponding && editor) {
      editor.commands.focus();
    }
  }, [isAgentResponding, editor]);

  // Restore user input when a run is cancelled or fails
  useEffect(() => {
    if (restoredMessage && editor) {
      editor.commands.setContent(restoredMessage.html);
      setAttachments(restoredMessage.attachments);
      editor.commands.focus();
    }
  }, [restoredMessage, editor]);

  // Add custom styles to the editor when mounted
  useEffect(() => {
    if (editor) {
      // Configure editor for proper content display
      const editorContent = editor.view.dom as HTMLElement;
      editorContent.style.overflow = 'visible';
      editorContent.style.whiteSpace = 'pre-wrap';
    }
  }, [editor]);

  const handleFilesSelected = async (files: File[]) => {
    if (files.length === 0) return;

    // Filter files over 10MB
    const validFiles = files.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        console.error(`File ${file.name} is too large (over 10MB)`);
        return false;
      }
      return true;
    });

    // Create pending attachments with unique temporary IDs
    const newPendingAttachments = validFiles.map((file) => ({
      id: `pending_${Date.now()}_${file.name}`,
      name: file.name,
      size: file.size,
      file,
      isUploading: true,
    }));

    // Add the pending attachments to the state
    setPendingAttachments((prev) => [...prev, ...newPendingAttachments]);

    // Process each attachment individually but with Promise.all to handle one at a time
    await Promise.all(
      newPendingAttachments.map(async (pendingAttachment) => {
        try {
          const uploadedAttachment = await onUploadAttachment(
            pendingAttachment.file,
          );

          // Add to completed attachments
          setAttachments((prev) => [...prev, uploadedAttachment]);

          // Remove from pending list
          setPendingAttachments((prev) =>
            prev.filter((item) => item.id !== pendingAttachment.id),
          );
        } catch (error) {
          console.error(
            `Failed to upload file ${pendingAttachment.name}:`,
            error,
          );

          // Mark as failed but keep in the list to allow retry
          setPendingAttachments((prev) =>
            prev.map((item) =>
              item.id === pendingAttachment.id
                ? { ...item, isUploading: false }
                : item,
            ),
          );
        }
      }),
    );
  };

  // Setup clipboard paste handling
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (e.clipboardData?.files.length) {
        await handleFilesSelected(Array.from(e.clipboardData.files));
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const { editor: workspaceEditor } = useEditorContext();

  const handleSendMessage = async () => {
    if (editor?.isEmpty) return;

    // Check if there are any uploading attachments
    const hasUploadingAttachments = pendingAttachments.some(
      (att) => att.isUploading,
    );
    if (hasUploadingAttachments) {
      alert('Please wait for attachments to finish uploading');
      return;
    }

    if (isDisabled) {
      return;
    }

    if (editor && !isAgentResponding) {
      // Save the current content and attachments in case we need to restore them.
      // Trim trailing empty paragraphs — TipTap may process Enter (creating a
      // new <p>) before our keydown handler can preventDefault().
      const currentContent = editor.getHTML().replace(/<p>(<br\s*\/?>)?<\/p>$/, '');
      const currentAttachments = [...attachments];

      // Set sending state and clear editor and attachments immediately
      editor.commands.setContent('');
      setAttachments([]);

      let documentId = null;

      if (workspaceEditor) {
        const agentChatPlugin = workspaceEditor.plugins.get(
          AgentChatPlugin,
        ) as AgentChatPlugin;
        documentId = workspaceEditor.config.get('documentId') as string;
        agentChatPlugin.handleChatStarted();
      }

      try {
        await sendMessage(currentContent, currentAttachments, {
          documentId,
          exampleDemandLetterId,
          webSearchEnabled,
        });

        if (onFirstMessage) {
          setTimeout(() => {
            onFirstMessage();
          }, 1000);
        }
      } finally {
        if (workspaceEditor) {
          const agentChatPlugin = workspaceEditor.plugins.get(
            AgentChatPlugin,
          ) as AgentChatPlugin;
          await agentChatPlugin.handleChatEnded();
        }
      }
    }
  };

  // Handle keyboard shortcuts for editor
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isAgentResponding) return;

    // Send message on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      // Don't send if slash command suggestion popup is active
      if (isSuggestionActiveRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      handleSendMessage();
    }
    // Add newline on Shift+Enter — let TipTap handle via hardBreak
    else if (e.key === 'Enter' && e.shiftKey && editor) {
      // passthrough
    }
  };

  const handleAttachClick = () => {
    if (!isAgentResponding) {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files?.length) {
      await handleFilesSelected(Array.from(e.target.files));
      // Reset the input so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = async (attachmentId: string) => {
    if (isAgentResponding) return;

    try {
      await onDeleteAttachment(attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  };

  const handleRemovePendingAttachment = async (pendingId: string) => {
    if (isAgentResponding) return;

    // Just remove from UI, no server request needed
    setPendingAttachments((prev) => prev.filter((p) => p.id !== pendingId));
  };

  const handleDemandExampleSelected = async (demandId: string) => {
    setExampleDemandLetterId(demandId);
    setIsDemandSelectorOpen(false);

    // If we have content in the editor, send it as a One Shot message
    if (editor && !editor.isEmpty) {
      const currentContent = editor.getHTML();
      const currentAttachments = [...attachments];

      // Set sending state and clear editor and attachments immediately
      editor.commands.setContent('');
      setAttachments([]);

      let documentId = null;

      if (workspaceEditor) {
        const agentChatPlugin = workspaceEditor.plugins.get(
          AgentChatPlugin,
        ) as AgentChatPlugin;
        documentId = workspaceEditor.config.get('documentId') as string;
        agentChatPlugin.handleChatStarted();
      }

      try {
        await sendMessage(`[ONE_SHOT] ${currentContent}`, currentAttachments, {
          documentId,
          exampleDemandLetterId: demandId,
          webSearchEnabled,
        });
      } finally {
        if (workspaceEditor) {
          const agentChatPlugin = workspaceEditor.plugins.get(
            AgentChatPlugin,
          ) as AgentChatPlugin;
          await agentChatPlugin.handleChatEnded();
        }
      }
    }
  };

  const handleOneShotDemandSelectedWrapper = async (demandId: string) => {
    // First trigger the analysis
    await handleOneShotDemandSelected(demandId);
    // Then close the selector modal
    setIsOneShotSelectorOpen(false);
  };

  const handleDemandButtonClick = () => {
    if (!isAgentResponding && !isDisabled) {
      if (exampleDemandLetterId) {
        // Remove the selected example
        setExampleDemandLetterId(null);
      } else {
        // Open the selector modal
        setIsDemandSelectorOpen(true);
      }
    }
  };

  const isAnyFileUploading = pendingAttachments.some((att) => att.isUploading);

  const isFileUploadDisabled = useMemo(() => {
    return isAgentResponding || isDisabled;
  }, [isAgentResponding, isDisabled]);

  const isSendingDisabled = useMemo(() => {
    return isSending || isAgentResponding || isDisabled || isAnyFileUploading;
  }, [isSending, isAgentResponding, isDisabled, isAnyFileUploading]);

  return (
    <div className="flex justify-center w-full">
      <div
        className={`flex flex-col w-full max-w-3xl bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${isAgentResponding || isSubmittingSections ? 'opacity-70' : ''}`}
      >
        {/* Attachments */}
        {(attachments.length > 0 || pendingAttachments.length > 0) && (
          <div className="flex flex-wrap gap-2 px-3 pt-2 pb-1">
            {/* Pending attachments */}
            {pendingAttachments.map((pending) => (
              <AttachmentBadge
                key={pending.id}
                id={pending.id}
                name={pending.name}
                size={pending.size}
                isUploading={pending.isUploading}
                onRemove={handleRemovePendingAttachment}
              />
            ))}

            {/* Completed attachments */}
            {attachments.map((attachment) => (
              <AttachmentBadge
                key={attachment.id}
                id={attachment.id}
                name={attachment.name}
                size={attachment.size}
                onRemove={handleRemoveAttachment}
                url={attachment.attachment}
                contentType={attachment.content_type}
              />
            ))}
          </div>
        )}

        {/* Editor */}
        <div
          className="flex flex-col px-3 pb-1"
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="textbox"
          aria-label="Message input"
        >
          <div className="max-h-[200px] overflow-y-auto scrollbar smooth-scroll">
            <EditorContent editor={editor} />
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-100/70">
          <div className="flex items-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              multiple
              accept="image/*"
              className="hidden"
              disabled={isFileUploadDisabled || isSubmittingSections}
            />
            <Button
              type="button"
              plain
              className={`p-1 text-gray-400 ${isAgentResponding || isSubmittingSections ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-600'} transition-colors relative`}
              onClick={handleAttachClick}
              aria-label="Attach files"
              disabled={isFileUploadDisabled || isSubmittingSections}
            >
              <PaperclipIcon className="h-4 w-4" />
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  plain
                  className={`p-1 ml-1 ${
                    isAgentResponding || isSubmittingSections
                      ? 'opacity-50 cursor-not-allowed'
                      : exampleDemandLetterId
                        ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700'
                        : 'text-gray-400 hover:text-gray-600'
                  } transition-colors relative`}
                  onClick={handleDemandButtonClick}
                  aria-label={
                    exampleDemandLetterId
                      ? 'Remove selected demand example'
                      : 'Select demand example'
                  }
                  disabled={isFileUploadDisabled || isSubmittingSections}
                >
                  {exampleDemandLetterId ? (
                    <X className="h-4 w-4" />
                  ) : (
                    <Newspaper className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {exampleDemandLetterId ? (
                  <div className="space-y-1">
                    <div className="font-medium text-xs">
                      Selected:{' '}
                      {selectedDemandExample?.title || 'Untitled Example'}
                    </div>
                    <div className="text-xs opacity-75">
                      Click to remove selection
                    </div>
                  </div>
                ) : (
                  <div className="text-xs">
                    Select a demand example to reference
                  </div>
                )}
              </TooltipContent>
            </Tooltip>

            {/* Web Search Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                  disabled={isAgentResponding || isSubmittingSections}
                  className={`flex items-center gap-1.5 ml-1 px-1.5 py-1 rounded transition-colors ${
                    isAgentResponding || isSubmittingSections
                      ? 'opacity-50 cursor-not-allowed'
                      : webSearchEnabled
                        ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}
                  aria-label="Toggle web search"
                >
                  <Globe className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="text-xs">
                  {webSearchEnabled
                    ? 'Web search enabled - click to disable'
                    : 'Enable web search to allow FinchGPT to search the internet'}
                </div>
              </TooltipContent>
            </Tooltip>

            {/* Slash Commands */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setIsManageDialogOpen(true)}
                  disabled={isAgentResponding || isSubmittingSections}
                  className={`flex items-center justify-center ml-1 h-6 w-6 rounded transition-colors ${
                    isAgentResponding || isSubmittingSections
                      ? 'opacity-50 cursor-not-allowed'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}
                  aria-label="Slash commands"
                >
                  <span className="text-sm font-mono font-semibold leading-none">/</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <div className="text-xs">
                  Slash commands &mdash; type{' '}
                  <span className="font-mono font-medium">/</span> in the editor
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-2 ml-auto" />
          {isAgentResponding ? (
            <Button
              type="button"
              className="p-1 rounded-full"
              onClick={() => stopRun()}
              aria-label="Stop message"
            >
              <Square className="h-4 w-4 fill-current" />
            </Button>
          ) : (
            <Button
              type="submit"
              className="p-1 rounded-full"
              onClick={handleSendMessage}
              disabled={isSendingDisabled}
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Original Demand Example Selector Modal */}
      <Dialog
        open={isDemandSelectorOpen}
        onOpenChange={setIsDemandSelectorOpen}
      >
        <DialogContent className="w-[1300px] max-w-full">
          <DialogHeader>
            <DialogTitle>Select Demand Example</DialogTitle>
          </DialogHeader>
          <DemandExampleSelector
            onSelect={handleDemandExampleSelected}
            className="max-h-[70vh] overflow-hidden"
          />
          <DialogFooter>
            <Button onClick={() => setIsDemandSelectorOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One Shot Demand Example Selector Modal */}
      <Dialog
        open={isOneShotSelectorOpen}
        onOpenChange={setIsOneShotSelectorOpen}
      >
        <DialogContent className="w-[1300px] max-w-full">
          <DialogHeader>
            <DialogTitle>Select Demand Example for One Shot</DialogTitle>
          </DialogHeader>
          <DemandExampleSelector
            onSelect={handleOneShotDemandSelectedWrapper}
            className="max-h-[70vh] overflow-hidden"
          />
          <DialogFooter>
            <Button onClick={() => setIsOneShotSelectorOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading Dialog */}
      <Dialog open={isAnalyzingSections} onOpenChange={() => {}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Analyzing Document Sections</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            <p className="mt-4 text-muted-foreground">
              Please wait while we analyze the document sections...
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sections Dialog - Replaced with SectionAnalysis component */}
      {showSectionsDialog && analyzedSections && (
        <SectionAnalysis
          sections={analyzedSections}
          onClose={() => {
            console.log(
              '[MessageInput] SectionAnalysis onClose triggered. Setting showSectionsDialog to false.',
            );
            setShowSectionsDialog(false);
          }}
          onSubmit={handleSubmitSections}
          isSubmitting={isSubmittingSections}
        />
      )}

      {/* Slash Command Management Dialog */}
      <SlashCommandManageDialog
        open={isManageDialogOpen}
        onOpenChange={(isOpen) => {
          setIsManageDialogOpen(isOpen);
          if (!isOpen) editor?.commands.focus();
        }}
        zIndex={Z_INDEX_FINCH_ASSISTANT + 10}
        commands={slashCommands}
        onAdd={addCommand}
        onUpdate={updateCommand}
        onDelete={deleteCommand}
        onImport={importCommands}
      />

      {/* Slash Command Import Dialog */}
      {importPayload && (
        <SlashCommandImportDialog
          open={isImportDialogOpen}
          onOpenChange={setIsImportDialogOpen}
          zIndex={Z_INDEX_FINCH_ASSISTANT + 10}
          payload={importPayload}
          onConfirmImport={importCommands}
          onDismiss={dismissImport}
        />
      )}
    </div>
  );
};
